import { ArrowLeftOutlined, CloseOutlined } from '@ant-design/icons';
import { RadioInput } from '@mm/components';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import { EMAIL_MATCH_REGEX } from '../../../../common/constants/validations';
import { useSetNextOnboardingStep } from '../../SignupOnboarding/use-set-next-onboarding-step';
import { ReactComponent as Stars } from '../WebsiteReportSummary/Icons/stars.svg';
import { ReactComponent as Copy } from '../WebsiteReportSummary/Icons/copy.svg';
import styles from './SendToDeveloper.module.css';
import { usePromoterJSCode } from '../../../../editor/features/startBotPromoters/common/use-promoter-js-code';
import { EmailInput } from '../../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import { activeFacebookPageSelector } from 'packages/state/selectors/facebookPageSelector';

type InfoMessageType = 'success' | 'copy' | undefined;

const InfoMessage = ({
  showInfoMessage,
  setShowInfoMessage,
  email,
}: {
  showInfoMessage: InfoMessageType;
  setShowInfoMessage: (type: InfoMessageType) => void;
  email: string;
}) => {
  const { setNextOnboardingStep } = useSetNextOnboardingStep();

  const isAutomations = window?.location?.pathname?.includes('automations');
  const isIntegrations = window?.location?.pathname?.includes('integrations');

  const config = {
    success: {
      header: 'Success!',
      subhader: `We have sent all instructions to ${email}`,
      icon: <Stars className={styles.stars} />,
      secondaryButtonName: 'Send to one more',
      primaryButtonName: 'Go to Dashboard',
      onClick: () => {
        setNextOnboardingStep(OnboardingSignupStep.NONE);
      },
      onSecondaryClick: () => setShowInfoMessage(undefined),
    },
    copy: {
      header: 'Copied to clipboard!',
      subhader: 'Please send this text to your developer. And then...',
      icon: <Copy className={styles.stars} />,
      primaryButtonName: 'Go to Dashboard',
      secondaryButtonName: 'Back',
      onClick: () => {
        setNextOnboardingStep(OnboardingSignupStep.NONE);
      },
      onSecondaryClick: () => setShowInfoMessage(undefined),
    },
  };
  if (!showInfoMessage) {
    return <></>;
  }
  return (
    <>
      {config[showInfoMessage].icon}
      <div className={styles.title}>
        <div className={styles.heading}>{config[showInfoMessage].header} </div>
        <div className={styles.subHeading}>
          {config[showInfoMessage].subhader}
        </div>
      </div>
      <div className={classNames(styles.buttons, styles.centered)}>
        {!isAutomations && !isIntegrations && (
          <PrimaryButton
            className={styles.button}
            onClick={() => config[showInfoMessage].onClick()}
          >
            {config[showInfoMessage].primaryButtonName}
          </PrimaryButton>
        )}

        <PrimaryButton
          className={styles.cancelButton}
          onClick={() => config[showInfoMessage].onSecondaryClick()}
        >
          <ArrowLeftOutlined />
          {config[showInfoMessage].secondaryButtonName}
        </PrimaryButton>
      </div>
    </>
  );
};

export const SendToDeveloper = ({
  promoter,
  onClose,
  onClick,
}: {
  promoter: PromoterModel;
  onClose: () => void;
  onClick?: () => void;
}) => {
  const facebookPage = useSelector(
    (state: StoreState) =>
      state.connectedPage.entities[0] || activeFacebookPageSelector(state),
  );
  const [email, setEmail] = useState('');
  const [sendByEmail, setSendByEmail] = useState(true);
  const [showInfoMessage, setShowInfoMessage] =
    useState<InfoMessageType>(undefined);

  const { update } = usePromoterCRUDActions();
  const promoterJSCode = usePromoterJSCode(promoter);

  const updatePromoter = async () => {
    if (promoter) {
      await update(facebookPage.activeBotId, {
        ...promoter,
        sendToDev: email,
      });
      setShowInfoMessage('success');
      onClick?.();
    }
  };
  // tslint:disable max-line-length
  const copyText = `Hello,
  In order to successfully capture details from your anonymous website visitors your javascript must be placed on your website.
  Your instructions for installing Customers.ai X-Ray Lead Generation Pixel are below.
  Paste the code below immediately after the <head> tag on every page you want your chat widget to appear.

  Instructions for installing the script with Google Tag Manager (recommended) or WordPress can be found at https://customers.ai/help/l/en/article/16hdqqv4tu-x-ray-installation-guide.
  
  ${promoterJSCode}`;

  return (
    <div className={styles.container}>
      <CloseOutlined className={styles.closeIcon} onClick={onClose} />

      {!showInfoMessage && (
        <>
          <div className={styles.title}>
            <div className={styles.heading}>
              Send directions to my developer{' '}
            </div>
          </div>

          <div className={styles.form}>
            <div className={styles.option}>
              <RadioInput
                css={{
                  input: styles.radioInput,
                  labelTitle: classNames(styles.radioInputLabel),
                }}
                name="email"
                checked={sendByEmail}
                value="email"
                label="Via email"
                onClick={() => setSendByEmail(true)}
              />
              {sendByEmail && (
                <>
                  <EmailInput
                    inputProps={{
                      placeholder: '',
                      style: { border: '1px solid #D9D9D9', minHeight: '40px' },
                    }}
                    onChange={p => setEmail(p.email)}
                    className={styles.input}
                    withIcon={false}
                    strict
                  />

                  <PrimaryButton
                    className={styles.button}
                    onClick={() => updatePromoter()}
                    disabled={!email || !EMAIL_MATCH_REGEX.test(email)}
                  >
                    Send
                  </PrimaryButton>
                </>
              )}
            </div>

            <div className={styles.option}>
              <RadioInput
                css={{
                  input: styles.radioInput,
                  labelTitle: classNames(styles.radioInputLabel),
                }}
                name="copy"
                checked={!sendByEmail}
                value="copy"
                label="Copy developer instructions"
                onClick={() => setSendByEmail(false)}
              />
              {!sendByEmail && (
                <>
                  <div className={styles.copyBlock}>
                    <div>Hello,</div>
                    <div>
                      In order to successfully capture details from your
                      anonymous website visitors your javascript must be placed
                      on your website. Your instructions for installing
                      Customers.ai X-Ray Lead Generation Pixel are below.
                    </div>
                    <div>
                      Paste the code below immediately after the{' '}
                      <strong>{'<head>'}</strong> tag on every page you want
                      your chat widget to appear.
                    </div>
                    <div>
                      Instructions for installing the script with Google Tags
                      manager (recommended) or WordPress can be found{' '}
                      <a
                        target="_blank"
                        href="https://customers.ai/help/l/en/article/16hdqqv4tu-x-ray-installation-guide"
                        className={styles.link}
                      >
                        HERE.
                      </a>
                    </div>
                    <div style={{ fontStyle: 'italic' }}>{promoterJSCode}</div>
                  </div>

                  <PrimaryButton
                    className={styles.button}
                    onClick={() => {
                      navigator.clipboard.writeText(copyText);
                      setShowInfoMessage('copy');
                      onClick?.();
                    }}
                  >
                    Copy
                  </PrimaryButton>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {showInfoMessage && (
        <InfoMessage
          showInfoMessage={showInfoMessage}
          setShowInfoMessage={setShowInfoMessage}
          email={email}
        />
      )}
    </div>
  );
};
